import { Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../../../services/auth.service';
import { apiUrl } from '../../../../environments/environment';

declare const grecaptcha: any;

/**
 * BATCH 108 — the reCAPTCHA site key now comes from the SERVER's .env (GET /api/auth/recaptcha_config),
 * so local, sandbox and production each use their own keys without a rebuild, and the server verifies
 * the token before sending any mail. No key on the server → no captcha box, and the form still works.
 */
@Component({
  selector: 'app-forgot-password',
  templateUrl: './forgot-password.component.html',
  styleUrls: ['./forgot-password.component.scss']
})
export class ForgotPasswordComponent implements OnInit, OnDestroy {

  @ViewChild('captchaBox', { static: true }) captchaBox: ElementRef<HTMLDivElement>;

  forgotForm: UntypedFormGroup = this.formBuilder.group({
    email: ['', [
      Validators.required,
      Validators.pattern("^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$")
    ]],
    myRecaptcha: [''],
  });

  // Form Errors
  errors = {
    email: [
      { type: 'required', message: ' Email is required.' },
      { type: 'pattern', message: ' Invalid Email format.' }
    ],
    myRecaptcha: [
      { type: 'required', message: ' Please tick "I\'m not a robot".' }
    ],
  }

  loading = false;
  /** null = not configured on this server (or not known yet). */
  siteKey: string | null = null;
  captchaError = '';
  private widgetId: any = null;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private authService: AuthService,
    private toastr: ToastrService,
    private http: HttpClient,
    private zone: NgZone
  ) { }

  async ngOnInit() {
    try {
      const r: any = await this.http.get(apiUrl + '/api/auth/recaptcha_config').toPromise();
      this.siteKey = (r && r.data && r.data.site_key) || null;
    } catch {
      this.siteKey = null;
    }
    if (!this.siteKey) return;   // server has no keys → no captcha required
    this.myRecaptcha.setValidators([Validators.required]);
    this.myRecaptcha.updateValueAndValidity();
    this.loadScript().then(() => this.renderCaptcha())
      .catch(() => this.captchaError = 'The captcha could not be loaded. Check your internet connection and reload the page.');
  }

  ngOnDestroy() {
    this.widgetId = null;
  }

  /** Google's script, loaded once per page with explicit rendering. */
  private loadScript(): Promise<void> {
    const w: any = window;
    if (w.grecaptcha && w.grecaptcha.render) return Promise.resolve();
    if (w.__vdRecaptchaLoading) return w.__vdRecaptchaLoading;
    w.__vdRecaptchaLoading = new Promise<void>((resolve, reject) => {
      w.__vdRecaptchaOnload = () => resolve();
      const s = document.createElement('script');
      s.src = 'https://www.google.com/recaptcha/api.js?onload=__vdRecaptchaOnload&render=explicit';
      s.async = true;
      s.defer = true;
      s.onerror = () => { w.__vdRecaptchaLoading = null; reject(); };
      document.head.appendChild(s);
    });
    return w.__vdRecaptchaLoading;
  }

  private renderCaptcha() {
    if (!this.captchaBox || this.widgetId !== null) return;
    this.widgetId = grecaptcha.render(this.captchaBox.nativeElement, {
      sitekey: this.siteKey,
      callback: (token: string) => this.zone.run(() => this.myRecaptcha.setValue(token)),
      'expired-callback': () => this.zone.run(() => this.myRecaptcha.setValue('')),
      'error-callback': () => this.zone.run(() => this.myRecaptcha.setValue('')),
    });
  }

  /** A token works once — after a refused request the box must be ticked again. */
  private resetCaptcha() {
    this.myRecaptcha.setValue('');
    try { if (this.widgetId !== null) grecaptcha.reset(this.widgetId); } catch {}
  }

  get email() {
    return this.forgotForm.get('email');
  }
  get myRecaptcha(){
    return this.forgotForm.get('myRecaptcha');
  }

  async onSubmit() {
    if(this.forgotForm.invalid) {
      this.email.markAsDirty();
      this.myRecaptcha.markAsDirty();
      this.toastr.error('Form contains error. Please check the form again.', 'Error!', {
        timeOut: 5000,
      });
      return;
    }
    this.loading = true;
    let mail = await this.authService.forgotPassword(this.email.value, this.myRecaptcha.value || null);
    this.loading = false;
    if(!mail) this.resetCaptcha();
  }

}
